import { isEmpty, isUndefined } from 'lodash/fp';

import { ApplicationState } from '../../../../store/types';
import { UserRole } from '../../../constants/permittedRoles';
import { ColumnSort, SortOrder, TableFilters } from '../../../shared/table/types';
import { ClientDocumentGroup, DocumentNameDB } from '../../documents/store/types';
import { EntityType } from '../../entity/store/types';
import { AccessGroup, AccessGroupEntity, AccessGroupSection, AccessGroupTableName } from './types';
import { ClientUser } from '../../users/store/types';
import { Role } from '../../roles/store/types';
import { getAccessGroupsRoot } from '../../store/selectors';

export const getAllAccessGroups = (state: ApplicationState): AccessGroup[] => getAccessGroupsRoot(state).allAccessGroups;

export const getIsFetchingAccessGroups = (state: ApplicationState): boolean => getAccessGroupsRoot(state).fetchingAccessGroups;

export const getCurrentAccessGroup = (state: ApplicationState): AccessGroup => getAccessGroupsRoot(state).currentAccessGroup;

export const getSavedAccessGroup = (state: ApplicationState): AccessGroup => getAccessGroupsRoot(state).savedAccessGroup;

export const getAccessGroupModalOpen = (state: ApplicationState): boolean => getAccessGroupsRoot(state).accessGroupModalOpen;

export const getAccessGroupSection = (state: ApplicationState): AccessGroupSection => getAccessGroupsRoot(state).selectedAccessGroupSection;

export const getEntitiesForAccessGroups = (state: ApplicationState): AccessGroupEntity[] => getAccessGroupsRoot(state).entitiesForAccessGroups;

export const getAccessGroupColumnSort = (table: AccessGroupTableName) => (state: ApplicationState): ColumnSort | undefined => {
    switch (table) {
        case AccessGroupTableName.DOCUMENT_GROUP:
            return getAccessGroupsRoot(state).accessGroupDocumentGroupColumnSort;
        case AccessGroupTableName.DOCUMENT:
            return getAccessGroupsRoot(state).accessGroupDocumentColumnSort;
        case AccessGroupTableName.ENTITY:
            return getAccessGroupsRoot(state).accessGroupEntityColumnSort;
        case AccessGroupTableName.USER:
            return getAccessGroupsRoot(state).accessGroupUserColumnSort;
        case AccessGroupTableName.ROLE:
            return getAccessGroupsRoot(state).accessGroupRoleColumnSort;
        default:
            return undefined;
    }
};

export const getAccessGroupFilters = (table: AccessGroupTableName) => (state: ApplicationState): TableFilters => {
    switch (table) {
        case AccessGroupTableName.DOCUMENT_GROUP:
            return getAccessGroupsRoot(state).accessGroupDocumentGroupFilters;
        case AccessGroupTableName.DOCUMENT:
            return getAccessGroupsRoot(state).accessGroupDocumentFilters;
        case AccessGroupTableName.ENTITY:
            return getAccessGroupsRoot(state).accessGroupEntityFilters;
        case AccessGroupTableName.USER:
            return getAccessGroupsRoot(state).accessGroupUserFilters;
        case AccessGroupTableName.ROLE:
            return getAccessGroupsRoot(state).accessGroupRoleFilters;
        default:
            return {};
    }
};

export const getFilteredAccessGroupUsers = (state: ApplicationState): ClientUser[] => {
    const allUsers = state.admin.users.allUsers;
    const columnSort = getAccessGroupColumnSort(AccessGroupTableName.USER)(state);
    const { userIds } = getCurrentAccessGroup(state);
    const userIsInGroup = (userId: number) => userIds.includes(userId) ? 1 : 0;
    let sortedUsers = allUsers.filter(({ role }) => role !== UserRole.SYSTEM_ADMIN);
    if (!isUndefined(columnSort) && columnSort.field === 'userIncluded') {
        sortedUsers = sortedUsers.sort((a, b) => columnSort.order === SortOrder.ASCENDING ? userIsInGroup(a.userId) - userIsInGroup(b.userId) : userIsInGroup(b.userId) - userIsInGroup(a.userId));
    }
    return sortedUsers;
};

export const getFilteredAccessGroupEntities = (state: ApplicationState): AccessGroupEntity[] => {
    const allEntities = getEntitiesForAccessGroups(state);
    const columnSort = getAccessGroupColumnSort(AccessGroupTableName.ENTITY)(state);
    const filters = getAccessGroupFilters(AccessGroupTableName.ENTITY)(state);
    const { entityIds } = getCurrentAccessGroup(state);
    const entityInGroup = (entityId: number) => entityIds.includes(entityId) ? 1 : 0;
    const groupEntityInGroup = (groupEntities: number[]) => groupEntities.every(entityId => entityIds.includes(entityId)) ? 1 : 0;
    const checkEntityInGroup = (entity: AccessGroupEntity) => entity.type === EntityType.GROUP ? groupEntityInGroup(entity.groupEntities) : entityInGroup(entity.entityId);
    let sortedEntities = allEntities;
    if (isUndefined(columnSort) && isEmpty(filters)) {
        const groupEntities = allEntities.filter(({ type }) => type === EntityType.GROUP);
        const companyEntities = allEntities.filter(({ type }) => type === EntityType.COMPANY);
        sortedEntities = [...groupEntities, ...companyEntities];
    }
    if (!isUndefined(columnSort) && columnSort.field === 'entityIncluded') {
        sortedEntities = allEntities.sort((a, b) => columnSort.order === SortOrder.ASCENDING ? checkEntityInGroup(a) - checkEntityInGroup(b) : checkEntityInGroup(b) - checkEntityInGroup(a));
    }
    return sortedEntities;
};

export const getAccessGroupDocuments = (state: ApplicationState): DocumentNameDB[] => {
    const allDocumentNames = getAccessGroupsRoot(state).accessGroupDocumentNames;
    const columnSort = getAccessGroupColumnSort(AccessGroupTableName.DOCUMENT)(state);
    const { documentNameIds } = getCurrentAccessGroup(state);
    const documentInGroup = (documentNameId: number) => documentNameIds.includes(documentNameId) ? 1 : 0;
    let sortedDocuments = allDocumentNames;
    if (!isUndefined(columnSort) && columnSort.field === 'documentIncluded') {
        sortedDocuments = sortedDocuments.sort((a, b) => columnSort.order === SortOrder.ASCENDING ? documentInGroup(a.documentNameId!) - documentInGroup(b.documentNameId!) : documentInGroup(b.documentNameId!) - documentInGroup(a.documentNameId!));
    }
    return sortedDocuments;
};

export const getAccessGroupDocumentGroups = (state: ApplicationState): ClientDocumentGroup[] => {
    const allDocumentGroups = getAccessGroupsRoot(state).allAccessGroupDocumentGroups;
    const columnSort = getAccessGroupColumnSort(AccessGroupTableName.DOCUMENT_GROUP)(state);
    const { documentNameIds } = getCurrentAccessGroup(state);
    const groupEntityInGroup = (groupDocumentNameIds: number[]) => groupDocumentNameIds.every(entityId => documentNameIds.includes(entityId)) ? 1 : 0;
    let sortedDocumentGroups = allDocumentGroups;
    if (!isUndefined(columnSort) && columnSort.field === 'groupIncluded') {
        sortedDocumentGroups = sortedDocumentGroups.sort((a, b) => columnSort.order === SortOrder.ASCENDING ? groupEntityInGroup(a.documentNameIds) - groupEntityInGroup(b.documentNameIds) : groupEntityInGroup(b.documentNameIds) - groupEntityInGroup(a.documentNameIds));
    }
    return sortedDocumentGroups;
};

export const getAccessGroupRoles = (state: ApplicationState): Role[] => {
    const allRoles: Role[] = state.admin.roles.allRoles;
    const columnSort = getAccessGroupColumnSort(AccessGroupTableName.ROLE)(state);
    const filter = getAccessGroupFilters(AccessGroupTableName.ROLE)(state);
    const { roleIds } = getCurrentAccessGroup(state);
    const roleInGroup = (roleId: number) => roleIds.includes(roleId) ? 1 : 0;
    let sortedRoles = allRoles;
    if (!isUndefined(columnSort)) {
        if (columnSort.field === 'roleIncluded') {
            sortedRoles = sortedRoles.sort((a, b) => columnSort.order === SortOrder.ASCENDING ? roleInGroup(a.roleId!) - roleInGroup(b.roleId!) : roleInGroup(b.roleId!) - roleInGroup(a.roleId!));
        } else {
            sortedRoles = sortedRoles.sort((a, b) => columnSort.order === SortOrder.ASCENDING ? a.roleName.localeCompare(b.roleName) : b.roleName.localeCompare(a.roleName));
        }
    }
    if (!isUndefined(filter.roleName)) {
        sortedRoles = sortedRoles.filter(({ roleName }) => roleName.toLowerCase().includes(filter.roleName.text.toLowerCase()));
    }
    return sortedRoles;
};

export const getAccessGroupNameIsValid = (state: ApplicationState): boolean => getAccessGroupsRoot(state).groupNameIsUnique;

export const getAccessGroupUpdated = (state: ApplicationState): boolean => getAccessGroupsRoot(state).accessGroupUpdated;

export const getIsSavingAccessGroup = (state: ApplicationState): boolean => getAccessGroupsRoot(state).isSavingAccessGroup;

export const getIsFetchingAccessGroupEntities = (state: ApplicationState): boolean => getAccessGroupsRoot(state).isFetchingEntities;

export const getIsFetchingAccessGroupDocumentNames = (state: ApplicationState): boolean => getAccessGroupsRoot(state).isFetchingDocuments;

export const getIsFetchingAccessGroupDocumentGroups = (state: ApplicationState): boolean => getAccessGroupsRoot(state).isFetchingDocumentGroups;

export const getDeleteConfirmationDialogOpen = (state: ApplicationState): boolean => getAccessGroupsRoot(state).deleteConfirmationDialogOpen;

export const getDeletingAccessGroup = (state: ApplicationState): boolean => getAccessGroupsRoot(state).deletingAccessGroup;

